import { useRef, useState, useEffect, useCallback, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import CreateJs from 'preload-js'
import HTMLParse from 'react-html-parser'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import FeaturedCards from '@/components/FeaturedCards'
import FeaturedPosts from '@/components/FeaturedPosts'
import FeaturedLinks from '@/components/FeaturedLinks'
import FeaturedIconCards from '@/components/FeaturedIconCards'
import SingleCard from '@/components/SingleCard'
// import CarouselDots from '@/components/CarouselDots'
import Highlights from '@/components/Highlights'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import style from './style'
import Popup from '../../components/Popup'

const useStyles = createUseStyles(style)

const HomePage = ({
  stub,
  cpt: cptFromProps,
}) => {
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  // const [insurance, setInsurance] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const { getImageUrl, setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { categories, current, page, posts, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    pages: state.page.pages,
    listCpt: state.page.archive[cptFromProps],
    posts: state.page.posts || {},
    strings: state.language.string,
    categories: state.page.taxonomies.categories || [],
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  // const getCPT = useCallback((cpt, index) => dispatch(actions.page.getListCPT(cpt, index)), [dispatch])
  const getPost = useCallback((numItem, paged) => dispatch(actions.page.getListPost(numItem, paged)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const featuredImg = getImageUrl(page.featured_media, 'full', stub, 'pages')
    preload.current.loadFile(featuredImg)
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) preloadMedia()
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    if (Object.keys(page).length === 0) getPage(stub)
    // if (!listCpt) getCPT(cptFromProps)
    if (Object.keys(posts).length === 0) getPost(2)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Set Insurance
  ------------------------------*/
  // useEffect(() => {
  //   if (listCpt !== undefined) {
  //     const listMapped = listCpt.map((cat) => ({
  //       id: pages[cat].id,
  //       src: getImageUrl(pages[cat].featured_media, 'large', cat, 'pages'),
  //       height: pages[cat].media_post[pages[cat].featured_media].height,
  //       width: pages[cat].media_post[pages[cat].featured_media].width,
  //       ratio: 1,
  //     }))
  //     setInsurance(listMapped)
  //   }
  // }, [listCpt])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderCoverHome = () => {
    return (
      <Hero
        img={getImageUrl(page.featured_media, 'full', stub, 'pages')}
        poster={getImageUrl(page.main_hero_video_poster_img, 'full', stub, 'pages')}
        video={page.main_hero_video_url}
        title={page.title.rendered}
        locator
        top
        className={classes.hero}
        homepage
      />
    )
  }

  /*------------------------------
  Render Featured Service 1
  ------------------------------*/
  const renderFeaturedService1 = () => {
    return page.related_carrozzeria?.length > 0 && (
      <FeaturedCards
        pretitle={page.related_carrozzeria_pretitle}
        title={page.related_carrozzeria_title}
        text={page.related_carrozzeria_text}
        label={page.related_carrozzeria_label}
        link={page.related_carrozzeria_link}
        items={page.related_carrozzeria.map((item) => ({
          id: item.ID,
          img: item.cover.large?.[0],
          title: item.title,
          text: item.excerpt,
          link: item.link,
        }))}
        root="dark"
      />
    )
  }

  /*------------------------------
  Render Featured Links
  ------------------------------*/
  const renderFeaturedLinks = () => {
    return page.links_list?.length > 0 && (
      <FeaturedLinks
        items={page.links_list.slice(0, 3).map((item) => ({
          id: item.id,
          img: getImageUrl(item.img, 'medium', stub),
          title: item.label,
          text: HTMLParse(item.text),
          link: item.link,
        }))}
      />
    )
  }

  /*------------------------------
  Render Featured Cards Icon
  ------------------------------*/
  const renderFeaturedIconCards = () => {
    return page.cards_list?.length > 0 && (
      <FeaturedIconCards
        title={page.convenient_title}
        text={page.convenient_text}
        link={page.convenient_link}
        items={page.cards_list.map((item, i) => ({
          id: i,
          icon: item.icon,
          text: item.text,
          background: i % 2 === 0 ? 'primary' : 'secondary',
          y: (i === 0 || i === 4 ? '5%' : '') || (i === 1 || i === 3 ? '10%' : ''),
          rotate: (i === 0 ? '10deg' : '') || (i === 1 ? '-20deg' : '') || (i === 3 ? '20deg' : '') || (i === 4 ? '-10deg' : ''),
        }))}
      />
    )
  }

  /*------------------------------
  Render Featured Posts
  ------------------------------*/
  const renderFeaturedPosts = () => {
    return Object.keys(posts).length > 0 && (
      <FeaturedPosts
        items={Object.values(posts).slice(0, 2).map((item) => ({
          id: item.id,
          img: getImageUrl(item.featured_media, 'medium', item.slug, 'posts'),
          title: item.title.rendered,
          text: HTMLParse(item.excerpt.rendered),
          link: item.link,
          cat: categories.length > 0 ? categories.find((cat) => cat.id === item.categories[0])?.name : '',
        }))}
      />
    )
  }

  /*------------------------------
  Render Featured Service 2
  ------------------------------*/
  const renderFeaturedService2 = () => {
    return page.related_interni?.length > 0 && (
      <FeaturedCards
        pretitle={page.related_interni_pretitle}
        title={page.related_interni_title}
        text={page.related_interni_text}
        label={page.related_interni_label}
        link={page.related_interni_link}
        items={page.related_interni.map((item) => ({
          id: item.ID,
          img: item.cover.large?.[0],
          title: item.title,
          text: item.excerpt,
          // link: item.link,
        }))}
        root="dark"
      />
    )
  }

  /*------------------------------
  Render Single Card
  ------------------------------*/
  const renderSingleCard = () => {
    return page.method_list.length > 0 && (
      <SingleCard
        title={page.method_title}
        label={page.method_label}
        link={page.method_link}
        list={page.method_list}
        img={getImageUrl(page.method_img, 'full', stub, 'pages')}
      />
    )
  }

  /*------------------------------
  Render Carousel Dots
  ------------------------------*/
  // const renderCarouselDots = () => {
  //   return insurance.length > 0 && (
  //     <CarouselDots
  //       title={page.assicurazioni_title}
  //       text={page.assicurazioni_text}
  //       label={page.assicurazioni_label}
  //       link={page.assicurazioni_link}
  //       items={insurance}
  //     />
  //   )
  // }

  /*------------------------------
  Render Highlights
  ------------------------------*/
  const renderHighlights = () => {
    return (
      <Highlights
        title={page.highlights_title}
        text={page.highlights_text}
        items={page.highlights_list}
        label={page.highlights_label}
        link={page.highlights_link}
      />
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <div className={classes.pageContent}>
        {renderCoverHome()}
        {renderFeaturedLinks()}
        {renderFeaturedService1()}
        {renderFeaturedIconCards()}
        {renderFeaturedPosts()}
        {renderFeaturedService2()}
        {renderSingleCard()}
        {/* {renderCarouselDots()} */}
        {renderHighlights()}
        {renderFooter()}
      </div>
    )
  }

  return (
    <>
      {load && (
      <Popup
        open={true}
        closeLabel={strings.close_label}
        link={strings.link_modal}
        image={strings.img_modal}
      />
      )}
      <LocomotiveScroll
        init={load}
        className={`page pageHome ${classes.root}`}
        ref={$root}
      >
        {renderHelmet()}
        {renderContent()}
      </LocomotiveScroll>
    </>
  )
}

export default HomePage
